import api from "@/config/api";
import { recoverTokenInfo } from "./recoverTokenInfo";
import { addPendingRecordingDeletion, getPendingRecordingDeletions, removePendingRecordingDeletion } from "./pendingRecordingDeletions";
import { AxiosError } from "axios";
import { appLogger } from "./AppLogger";
import { AuthHelpers } from "@/lib/auth/AuthHelpers";

export interface SubjectOffsetPair {
  recordingSubjectId: string;
  offSet: string;
}

export const deleteCurrentRecording = async (deviceKey: string, recordingId: string | undefined) => {
  try {
    const initialToken = recoverTokenInfo();

    return await api.delete(`/v0/recording/${recordingId}`, {
      headers: {
        Authorization: `Bearer ${initialToken}`,
        "X-iBabs-Device-Id": deviceKey,
      },
    });
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(`Delete recording failed: ${error.response?.status ?? error.status ?? "unknown"}`);
    }

    throw error instanceof Error ? error : new Error("Delete recording failed");
  }
};

const DELETE_QUICK_RETRY_LIMIT = 3;
const DELETE_BASE_BACKOFF_MS = 3000;
const DELETE_MAX_BACKOFF_MS = 30000;

// See docs/recording-upload-retry.md for the full retry/persistence design.
function waitForOnline(ms: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("online", finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    window.addEventListener("online", finish);
  });
}

// Deletes an orphaned recording, retrying until it succeeds. See docs/recording-upload-retry.md.
export const deleteMeetingRecording = async (deviceKey: string, recordingId: string, token: string): Promise<void> => {
  addPendingRecordingDeletion(recordingId, deviceKey);

  let attempt = 1;

  for (;;) {
    try {
      const response = await fetch(`${import.meta.env.VITE_SMART_DEBRIEF_API}/v0/recording/${recordingId}`, {
        method: "DELETE",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-iBabs-Device-Id": deviceKey,
        },
        credentials: "include",
      });

      if (response.ok) {
        removePendingRecordingDeletion(recordingId);
        return;
      }

      // A 4xx (other than 429, which just means "back off and retry") means the request itself
      // is invalid - e.g. the recording is already gone server-side - so retrying it verbatim
      // is unlikely to ever succeed. Give it the same DELETE_QUICK_RETRY_LIMIT attempts as a
      // transient failure (in case it's a momentary server-side inconsistency), then give up
      // instead of looping on the same failure forever via the connectivity-backoff path below.
      const isPermanentFailure = response.status >= 400 && response.status < 500 && response.status !== 429;

      if (isPermanentFailure && attempt >= DELETE_QUICK_RETRY_LIMIT) {
        appLogger(`Giving up deleting orphaned recording after repeated permanent failures`, {
          type: "error",
          errorReportOptions: { eventName: "DELETE-MEETING-RECORDING", properties: { recordingId, attempt, statusCode: response.status } },
        });
        removePendingRecordingDeletion(recordingId);
        return;
      }

      // A 5xx could be a momentary server-side blip, but it could just as easily be a
      // persistent bug on a specific recording - unlike a network error, retrying that
      // indefinitely via the connectivity-backoff path below would hammer the endpoint
      // forever. Stop after the same quick-retry budget, but leave the entry pending so
      // retryPendingRecordingDeletions picks it up again on a later sweep (a future app
      // load, once the server-side issue is presumably fixed).
      const isServerError = response.status >= 500;

      if (isServerError && attempt >= DELETE_QUICK_RETRY_LIMIT) {
        appLogger(`Giving up deleting orphaned recording after repeated server errors, will retry on next sweep`, {
          type: "error",
          errorReportOptions: { eventName: "DELETE-MEETING-RECORDING", properties: { recordingId, attempt, statusCode: response.status } },
        });
        return;
      }

      throw new Error(`Delete recording failed: ${response.status}`);
    } catch (error) {
      if (attempt <= DELETE_QUICK_RETRY_LIMIT) {
        console.log(`deleteMeetingRecording: attempt ${attempt} failed, retrying shortly:`, error);
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        attempt += 1;
        continue;
      }

      console.log(`deleteMeetingRecording: attempt ${attempt} failed, waiting for connectivity to retry:`, error);
      await waitForOnline(Math.min(DELETE_BASE_BACKOFF_MS * 2 ** (attempt - DELETE_QUICK_RETRY_LIMIT - 1), DELETE_MAX_BACKOFF_MS));
      attempt += 1;
    }
  }
};

// Resumes deletions pending from a previous session. See docs/recording-upload-retry.md.
export async function retryPendingRecordingDeletions(onDeleted?: (recordingUUID: string) => void): Promise<void> {
  const pending = getPendingRecordingDeletions();
  if (!pending.length) return;

  const token = recoverTokenInfo();
  // Same reasoning as the token above: deviceKey lives in sessionStorage, so a new
  // session (new tab, or the browser reopened) generates a new one and the entry's
  // deviceKey - captured at the original failed attempt - is stale. Re-resolve it
  // the same way useDeviceKey does instead of trusting entry.deviceKey.
  const deviceKey = AuthHelpers.getGetOrCreateKey(import.meta.env.VITE_IBABS_AUTH_DEVICE_KEY);

  pending.forEach((entry) => {
    deleteMeetingRecording(deviceKey, entry.recordingUUID, token)
      .then(() => onDeleted?.(entry.recordingUUID))
      .catch((error) => {
        console.log(`retryPendingRecordingDeletions: failed to resume delete for ${entry.recordingUUID}:`, error);
      });
  });
}

export const deleteRecordingRows = async (deviceKey: string, recordingIds: string[], token: string) => {
  return await api.delete("/v0/recording", {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-iBabs-Device-Id": deviceKey,
    },
    data: {
      recordingIds,
    },
  });
};
